import jQuery from 'jquery';

import { 
  map, 
  sum, 
  range,
  flatMap,
  startsWith,
  endsWith,
} from 'lodash';

// https://github.com/nliautaud/gedcom-svg-fanchart
export function degreesToRadians(degrees) {
  return degrees * Math.PI / 180;
}

export function radiansToDegrees(radians) {
  return radians * 180 / Math.PI;
}


export function circlePathDef(x, y, r) {
  return [
    `M${x+r},${y}`,
    `A${r},${r} 0 0,0 ${x-r},${y}`,
    `A${r},${r} 0 0,0 ${x+r},${y}`
  ].join(' ');
}

export function slicePathDef(start, angle, radius, thickness) {
  const coord = slicePathCoordinates(start, angle, radius, thickness);

  return [
    `M${coord.arc1.a.x},${coord.arc1.a.y}`,
    `A${coord.arc1.r},${coord.arc1.r} 0 0,1 ${coord.arc1.b.x},${coord.arc1.b.y}`,
    `L${coord.arc2.a.x},${coord.arc2.a.y}`,
    `A${coord.arc2.r},${coord.arc2.r} 0 0,0 ${coord.arc2.b.x},${coord.arc2.b.y}`,
    `Z`
  ].join(' ');
}

export function slicePathCoordinates(start, angle, r, t, reverse) {
  const arc1 = arcCoordinates(start, angle, r, reverse);
  const arc2 = arcCoordinates(start + angle, -angle, r + t, reverse);
  return reverse ? { arc1: arc2, arc2: arc1 } : { arc1, arc2 };
}

export function arcCoordinates(start, angle, r, reverse) {
  const _start = reverse ? start + angle : start;
  const _angle = reverse ? -angle : angle;

  return {
    r,
    a : { x : Math.cos(_start) * r, y : Math.sin(_start) * r },
    b : { x : Math.cos(_start + _angle) * r, y : Math.sin(_start + _angle) * r }
  };
}

export function textArcPath(start, angle, r, reverse) {
  const c = arcCoordinates(start, angle, r, reverse);
  const arcdir = reverse ? '0,0' : '0,1';

  return [
    `M${c.a.x},${c.a.y}`,
    `A${r},${r} 0 ${arcdir} ${c.b.x},${c.b.y}`
  ].join(' ');
}


export function lowerCamelCaseToRegular(camelCase) {
  return camelCase.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
}


export function googleUrlShortener(url, onSuccess) {
  const data = {
    "dynamicLinkInfo": {
      "domainUriPrefix": "https://app.familytreeprints.com/l",
      "link": url,
      "navigationInfo": {
          "enableForcedRedirect": "1",
      },
    }
  }

  jQuery.ajax({
    url: `https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=AIzaSyAuvu2QGJV9hJl7hoyM0EHUDAlfW6rbtfk`,
    type: 'POST',
    contentType: 'application/json; charset=utf-8',
    data: JSON.stringify(data),
    dataType: 'json',
    success: (response) => onSuccess(response.shortLink)
  }, 'json');
}

export function colorToString(color) {
  const rgb = color.rgb;
  return `rgb(${rgb.r},${rgb.g},${rgb.b})`;
}

export function getDetailedChart(chart, generations) {
  const startRadian = degreesToRadians(chart.angleOffset + 180 - (chart.angle - 180) / 2);
  const totalRadians = degreesToRadians(chart.angle);
  const totalRadius = sum(map(generations.slice(0, chart.generations), 'thickness'))

  let startAngle = chart.angleOffset - chart.angle / 2;
  let endAngle = startAngle + chart.angle;
  const boundingBox = [0, 90, 180, 270, 360, 450, 540, -90].map(angle => {
    if(startAngle <= angle && endAngle >= angle) {
      return totalRadius;
    } else if(startAngle > angle + 90 && endAngle < angle + 180) {
      return 0;
    }
 
    return Math.max(
      0, 
      Math.cos(degreesToRadians(startAngle - angle)), 
      Math.cos(degreesToRadians(endAngle - angle))
    ) * totalRadius;
  })
  
  return {
    ...chart,
    startRadian,
    totalRadians,
    totalRadius,
    minY: Math.ceil(Math.max(boundingBox[0], boundingBox[4])),
    maxY: Math.ceil(Math.max(boundingBox[2], boundingBox[6])),
    maxX: Math.ceil(Math.max(boundingBox[1], boundingBox[5])),
    minX: Math.ceil(Math.max(boundingBox[3], boundingBox[7])),
  }
}

export function getDetailedCells(cells, generations, dChart) {
  return flatMap(range(0, dChart.generations), g => {
    const generation = generations[g];
    const n = Math.pow(2, g);
    const radians = dChart.totalRadians / n;
    
    return range(0, n).map(i => {
      const cell = cells[n + i] || {};
      return {
        ...cell,
        ascendancy: n + i,
        index: i,
        generation: g,
        startRadian: dChart.startRadian + radians * i,
        radians,
        innerRadius: sum(map(generations, 'thickness').slice(0, g)),
        thickness: generation.thickness,      
        isVertical: startsWith(generation.cellType, 'vertical'),
        isInverted: cell.inverted,
        cellType: generation.cellType,
      }
    })
  });
}

export function getBandPath(cell, width=10) {
  return slicePathDef(cell.startRadian, cell.radians, cell.innerRadius + cell.thickness - width + 5, width);
}

export function getCellPath(cell) {
  if(cell.generation === 0) {
    return circlePathDef(0, 0, cell.thickness);
  }
  return slicePathDef(cell.startRadian, cell.radians, cell.innerRadius, cell.thickness);
}

export function getTextPath(cell) {
  if(cell.generation === 0) {
    return `M${-cell.thickness},${0} ${cell.thickness},${0}`;
  }

  if(cell.isVertical) {
    const center = cell.startRadian + cell.radians / 2;
    const coord = slicePathCoordinates(center, 0, cell.innerRadius, cell.thickness, cell.isInverted);
    return `M${coord.arc1.a.x},${coord.arc1.a.y} ${coord.arc2.a.x},${coord.arc2.a.y}`;
  } 
    
  if(cell.cellType === 'horizontal-straight') {
    const center = cell.startRadian + cell.radians / 2;
    const coord = slicePathCoordinates(center, 0, cell.innerRadius, cell.thickness, cell.isInverted);

    const slopeY = coord.arc2.a.y - coord.arc1.a.y;
    const slopeX = coord.arc2.a.x - coord.arc1.a.x;

    const midX = coord.arc1.a.x + slopeX / 2.0;
    const midY = coord.arc1.a.y + slopeY / 2.0;

    const aX = midX - slopeY;
    const aY = midY + slopeX;
    const bX = midX + slopeY;
    const bY = midY - slopeX;
    return `M${bX},${bY} ${aX},${aY}`;
  }

  const center = cell.innerRadius + cell.thickness / 2;
  return textArcPath(cell.startRadian, cell.radians, center, cell.isInverted);
}


// multiline
  // space, \n, # = break
  // ~ = no break

// line
  // \n, # = break
  // ~, space = normal
export function convertToHumanReadable(cellType, value) {
  const multiline = endsWith(cellType, 'multiline');

  return multiline 
    ? value.replace(/[# \n]+/g, '\n').replace(/~+/g, ' ')
    : value.replace(/[\n#]+/g, '\n').replace(/[~ ]+/g, ' ')
}

export function convertFromHumanReadable(cellType, value) {
  const multiline = endsWith(cellType, 'multiline');

  return multiline
    ? value.replace(/ +/g, '~')
    : value.replace(/\n+/g, '#')
}


// WEBPACK FOOTER //
// ./src/core/utils/index.js